import { Trash2 } from "lucide-react";
import { useState } from "react";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaBadge } from "@/shared/components/data-display/ColocaBadge";
import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import { ColocaDrawer } from "@/shared/components/overlays/ColocaDrawer";
import useTranslations from "@/shared/lang/useTranslations";
import { formatDateTime } from "@/shared/utils/dateTimeHelpers";
import { compareDecimalStrings, trimTrailingZeros } from "@/shared/utils/decimal";

import type { PaymentTax, TaxScopeGroup } from "../../types/paymentTaxes.types";

export interface TaxHistoryDrawerProps {
  group: TaxScopeGroup;
  now: number;
  canWrite: boolean;
  /** Whose rule this is and where it applies. */
  scope: string;
  /** The other rows a shared rule applies to; deleting a version changes them too. */
  covers: string | null;
  /** Set when the version cannot be corrected from here, saying where it is. */
  readOnlyReason: string | null;
  isDeleting: boolean;
  error: string | null;
  onDelete: (tax: PaymentTax) => Promise<boolean>;
  onClose: () => void;
}

type VersionStatus = "in_force" | "scheduled" | "superseded";

/** Every version of one scope, newest first. Deleting one is a correction: the previous version rules again. */
export function TaxHistoryDrawer({
  group,
  now,
  canWrite,
  scope,
  covers,
  readOnlyReason,
  isDeleting,
  error,
  onDelete,
  onClose,
}: TaxHistoryDrawerProps) {
  const canDelete = canWrite && !readOnlyReason;
  const { t, activeLanguage } = useTranslations("paymentTaxes");
  const [confirming, setConfirming] = useState<string | null>(null);

  const statusOf = (tax: PaymentTax): VersionStatus =>
    new Date(tax.valid_from).getTime() > now
      ? "scheduled"
      : tax.id === group.current?.id
        ? "in_force"
        : "superseded";

  return (
    <ColocaDrawer
      open
      onOpenChange={(next) => {
        if (!next && !isDeleting) onClose();
      }}
      title={t("history_title", { name: group.name })}
      description={t("history_description")}
      pinLastChild={false}
    >
      <div className="mb-3 space-y-2">
        <p className="text-sm font-medium text-foreground">{scope}</p>
        {covers && <ColocaAlert variant="warning" text={covers} />}
        {canWrite && readOnlyReason && <ColocaAlert variant="secondary" text={readOnlyReason} />}
      </div>
      <ol className="space-y-3">
        {group.versions.map((tax) => {
          const status = statusOf(tax);
          return (
            <li key={tax.id} className="space-y-2 rounded-xl border border-border bg-card p-3">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-mono text-base font-semibold tabular-nums">
                  {compareDecimalStrings(tax.rate, "0") === 0
                    ? t("exempt")
                    : `${trimTrailingZeros(tax.rate)} %`}
                </span>
                <ColocaBadge
                  variant={status === "in_force" ? "success" : status === "scheduled" ? "warning" : "ghost"}
                >
                  {t(`history_${status}`)}
                </ColocaBadge>
                <span className="ml-auto text-xs text-muted-foreground">
                  {t("history_version", { version: String(tax.version) })}
                </span>
              </div>
              <p className="text-xs text-muted-foreground">
                {t(tax.bearer === "CLIENT" ? "bearer_client" : "bearer_coloca")} ·{" "}
                {t("since", { date: formatDateTime(tax.valid_from, { locale: activeLanguage }) })}
              </p>
              <p className="text-sm">{tax.change_reason || t("history_reason_none")}</p>
              {canDelete &&
                (confirming === tax.id ? (
                  <div className="space-y-2 rounded-lg bg-muted/50 p-2">
                    <p className="text-xs">{t("history_delete_description")}</p>
                    <div className="flex justify-end gap-2">
                      <ColocaButton
                        type="button"
                        variant="ghost"
                        size="sm"
                        disabled={isDeleting}
                        onClick={() => setConfirming(null)}
                      >
                        {t("button_cancel")}
                      </ColocaButton>
                      <ColocaButton
                        type="button"
                        variant="destructive"
                        size="sm"
                        loading={isDeleting}
                        onClick={async () => {
                          if (await onDelete(tax)) setConfirming(null);
                        }}
                      >
                        {t("history_delete_confirm")}
                      </ColocaButton>
                    </div>
                  </div>
                ) : (
                  <ColocaButton
                    type="button"
                    variant="ghost"
                    size="sm"
                    icon={Trash2}
                    onClick={() => setConfirming(tax.id)}
                  >
                    {t("history_delete")}
                  </ColocaButton>
                ))}
            </li>
          );
        })}
      </ol>
      {error && <ColocaAlert variant="destructive" text={error} className="mt-3" />}
    </ColocaDrawer>
  );
}
